import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api, { authAPI } from '../../services/api';
import feedAlertsAdminAPI from '../../services/feedAlertsAdminAPI';
import WhatsNew from './WhatsNew';
import { usePageVisible } from '../../hooks/usePageVisible';
import './Header.css';


const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { canAccessAdminPortal, canAccessMillPortal, canAccessCRM, canAccessStorePortal, canManageDelivery, user: authUser, loading: authLoading } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pendingRegistrations, setPendingRegistrations] = useState(0);
  const [pendingTaxDocs, setPendingTaxDocs] = useState(0);
  const [pendingStoreClub, setPendingStoreClub] = useState(0);
  const [pendingTaxExemptions, setPendingTaxExemptions] = useState(0);
  const [feedAlertCount, setFeedAlertCount] = useState(0);
  const isVisible = usePageVisible();

  // Dropdown menu states
  const [salesMenuOpen, setSalesMenuOpen] = useState(false);
  const [operationsMenuOpen, setOperationsMenuOpen] = useState(false);
  const [productsMenuOpen, setProductsMenuOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  // Which Admin sub-group (accordion) is expanded. Single-open keeps the menu short.
  const [openAdminGroup, setOpenAdminGroup] = useState<string | null>(null);

  // Admin portal access from AuthContext — DB fields are single source of truth
  const isOfficeStaff = canAccessAdminPortal;
  // Managers+ (superuser or ADMIN role) — matches the announcement-authoring
  // page's server-side permission.
  const isManager = !!(authUser?.is_superuser || authUser?.role === 'ADMIN');

  useEffect(() => {
    if (authUser) {
      // Fetch pending registration count
      const fetchPendingCount = async () => {
        try {
          const response = await api.get('/admin/pending-registrations/count/');
          setPendingRegistrations(response.data.count);
        } catch (error) {
          // Silently fail - user may not have permission
        }
      };
      // Fetch pending tax documents count
      const fetchTaxDocsCount = async () => {
        try {
          const response = await api.get('/admin/tax-documents/summary/');
          setPendingTaxDocs(response.data.pending_count || 0);
        } catch (error) {
          // Silently fail - user may not have permission
        }
      };
      // Fetch pending Store Club members count
      const fetchStoreClubCount = async () => {
        try {
          const response = await api.get('/admin/store-club-members/stats/');
          setPendingStoreClub(response.data.pending_members || 0);
        } catch (error) {
          // Silently fail - user may not have permission
        }
      };
      const fetchFeedAlertCount = async () => {
        try {
          const counts = await feedAlertsAdminAPI.getCounts();
          setFeedAlertCount(counts.new);
        } catch (error) {
          // Silently fail - user may not have permission
        }
      };
      // Pending Bridge Club tax-exemption requests (for the Bridge Club badge).
      const fetchTaxExemptionCount = async () => {
        try {
          const response = await api.get('/admin/tax-exemption-requests/stats/');
          setPendingTaxExemptions(response.data.pending || 0);
        } catch (error) {
          // Silently fail - user may not have permission
        }
      };
      fetchPendingCount();
      fetchTaxDocsCount();
      fetchStoreClubCount();
      fetchFeedAlertCount();
      fetchTaxExemptionCount();
      // Refresh every 5 min (10 min when tab hidden)
      const ms = isVisible ? 300000 : 600000;
      const interval = setInterval(() => {
        if (!navigator.onLine) return;
        fetchPendingCount();
        fetchTaxDocsCount();
        fetchStoreClubCount();
        fetchFeedAlertCount();
        fetchTaxExemptionCount();
      }, ms);
      return () => clearInterval(interval);
    }
  }, [authUser, isVisible]);

  const handleLogout = async () => {
    // Call backend to clear httpOnly cookies, then clear localStorage
    await authAPI.logout();
    navigate('/login');
  };

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleNavClick = () => {
    setMobileMenuOpen(false);
    setSalesMenuOpen(false);
    setOperationsMenuOpen(false);
    setProductsMenuOpen(false);
    setAdminMenuOpen(false);
  };

  // Close all dropdowns
  const closeAllDropdowns = () => {
    setSalesMenuOpen(false);
    setOperationsMenuOpen(false);
    setProductsMenuOpen(false);
    setAdminMenuOpen(false);
  };

  // Dropdown hover delay — keep menu open briefly on mouse-out so users don't lose it
  const dropdownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleDropdownEnter = (setter: (v: boolean) => void) => {
    if (dropdownTimer.current) clearTimeout(dropdownTimer.current);
    closeAllDropdowns();
    setter(true);
  };

  const handleDropdownLeave = (setter: (v: boolean) => void) => {
    dropdownTimer.current = setTimeout(() => setter(false), 300);
  };

  useEffect(() => {
    return () => { if (dropdownTimer.current) clearTimeout(dropdownTimer.current); };
  }, []);

  // Check if any path in the dropdown is active
  const isDropdownActive = (paths: string[]) => {
    return paths.some(path => location.pathname.startsWith(path));
  };

  // Admin dropdown grouped into accordion sections. Every item keeps its EXACT
  // route + gate (`show`) from the old flat list — this is purely visual regroup.
  // `match` overrides the active-path check when the highlight path differs from
  // the link target (POS, Laura). `badge` shows a count on the item.
  interface AdminItem { to: string; label: React.ReactNode; show: boolean; match?: string[]; badge?: number }
  const adminGroups: { key: string; label: string; items: AdminItem[] }[] = [
    { key: 'people', label: 'People & Customers', items: [
      { to: '/users', label: 'Team', show: isOfficeStaff },
      { to: '/crm', label: 'CRM', show: true },
      { to: '/admin/bridge-club', label: 'Bridge Club', show: isOfficeStaff, badge: pendingTaxExemptions },
      { to: '/admin/agreements', label: 'Agreements', show: isOfficeStaff },
    ] },
    { key: 'marketing', label: 'Email & Marketing', items: [
      { to: '/crm/promo-campaigns', label: 'Promo Campaigns', show: true },
      { to: '/admin/partner-promotions', label: 'Partner Promotions', show: isOfficeStaff && isManager },
      { to: '/admin/announcements', label: "What's New", show: isOfficeStaff && isManager },
      { to: '/admin/emails', label: 'Email Portal', show: isOfficeStaff },
    ] },
    { key: 'operations', label: 'Operations', items: [
      { to: '/admin/pipeline-health', label: 'Pipeline Health', show: isOfficeStaff },
      { to: '/admin/in-transit', label: 'Shipment Tracking', show: isOfficeStaff },
      { to: '/admin/pos/products', label: 'POS / Store', show: isOfficeStaff, match: ['/admin/pos'] },
      { to: '/admin/bridge-club/store-ledger', label: 'Store Ledger', show: isOfficeStaff },
    ] },
    { key: 'integrations', label: 'Integrations & Tools', items: [
      { to: '/admin/partner-api', label: 'Partner API', show: isOfficeStaff },
      { to: '/admin/laura-learning', label: "Laura's Brain", show: true, match: ['/admin/laura-learning', '/knowledge'] },
      { to: '/admin/resources', label: 'Resources', show: isOfficeStaff },
    ] },
    { key: 'support', label: 'Support', items: [
      { to: '/admin/support-tickets', label: 'Support Tickets', show: isOfficeStaff },
      { to: '/admin/prayer-requests', label: 'Prayer Requests', show: !!authUser?.is_superuser },
    ] },
    // 'Settings' holds the items the 5-group spec didn't name (Security, QuickBooks,
    // Passkeys, Notifications) — kept so nothing is dropped in the regroup.
    { key: 'settings', label: 'Settings', items: [
      { to: '/admin/security', label: 'Security', show: isOfficeStaff },
      { to: '/settings/quickbooks', label: 'QuickBooks', show: isOfficeStaff },
      { to: '/settings/passkeys', label: 'Passkeys', show: isOfficeStaff },
      { to: '/settings/notifications', label: 'Notifications', show: isOfficeStaff },
    ] },
  ];
  const itemActive = (item: AdminItem) => (item.match || [item.to]).some(p => isActive(p));
  const groupHasActive = (g: { items: AdminItem[] }) => g.items.some(it => it.show && itemActive(it));

  // On open, auto-expand the group that holds the current page so its highlighted
  // item is visible immediately.
  useEffect(() => {
    if (adminMenuOpen) {
      const active = adminGroups.find(groupHasActive);
      setOpenAdminGroup(active ? active.key : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminMenuOpen]);

  return (
    <header className="rbf-header">
      {/* Row 1: Branding and User Actions */}
      <div className="header-top-bar">
        <div className="header-top-container">
          <div className="header-brand">
            <img src="/rbf-logo.png" alt="Red Bridge Farm Feed Co." className="header-logo" />
            <div className="header-brand-text">
              <span className="header-title">Red Bridge Farm Feed Co.</span>
              <span className="header-subtitle">Admin Portal</span>
            </div>
          </div>

          {authUser ? (
            <div className="header-user">
              <span className="user-welcome">Welcome, <strong>{authUser.username}</strong></span>
              <button onClick={() => window.open('/portal/login', '_blank')} className="btn-portal-switch btn-customer-portal">
                Customer Portal
              </button>
              {canAccessMillPortal && (
                <button onClick={() => navigate('/mill/dashboard')} className="btn-portal-switch">
                  Mill Portal
                </button>
              )}
              {canAccessStorePortal && (
                <button onClick={() => navigate('/store/dashboard')} className="btn-portal-switch">
                  Store
                </button>
              )}
              {/* Manager-only until the driver route view exists — a driver has no
                  page yet, so the nav stays hidden rather than lead nowhere. */}
              {canManageDelivery && (
                <button onClick={() => navigate('/delivery/dashboard')} className="btn-portal-switch">
                  Delivery Portal
                </button>
              )}
              <span className="header-whats-new"><WhatsNew portal="ADMIN" /></span>
              {/* btn-logout-bar: hidden wherever the hamburger menu (which has its
                  own Logout) exists. The fail-safe Logout below has no menu and
                  must never be hidden, so it does not carry this class. */}
              <button onClick={handleLogout} className="btn-logout btn-logout-bar">
                Logout
              </button>
              {/* Hamburger Menu Button - Mobile Only */}
              <button
                className="hamburger-btn"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle navigation menu"
              >
                <span className="hamburger-line"></span>
                <span className="hamburger-line"></span>
                <span className="hamburger-line"></span>
              </button>
            </div>
          ) : authLoading ? null : (
            // Fail-safe: auth resolved and there is no user. Never strand someone
            // with no way out — always offer a logout (a dead nav with no logout
            // is what turned a cosmetic bug into "she can't navigate anywhere").
            // Gated on !authLoading so the initial /auth/me round trip doesn't
            // flash a bare Logout button on every page load.
            <div className="header-user">
              <button onClick={handleLogout} className="btn-logout">
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Row 2: Navigation Menu */}
      {authUser && (
        <div className={`header-nav-bar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          {/* Mobile Close Button */}
          <button
            className="mobile-menu-close"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            ×
          </button>
          <div className="header-nav-container">
            <nav className="header-nav">
              {/* DASHBOARD - Standalone */}
              <Link to="/dashboard" className={`nav-link ${isActive('/dashboard') ? 'active' : ''}`} onClick={handleNavClick}>
                Dashboard
              </Link>

              {/* SALES Dropdown */}
              <div className="nav-dropdown"
                onMouseEnter={() => handleDropdownEnter(setSalesMenuOpen)}
                onMouseLeave={() => handleDropdownLeave(setSalesMenuOpen)}
              >
                <button
                  className={`nav-link nav-dropdown-trigger ${isDropdownActive(['/customers', '/orders', '/order-entry', '/invoice']) ? 'active' : ''}`}
                  onClick={() => { closeAllDropdowns(); setSalesMenuOpen(!salesMenuOpen); }}
                >
                  Sales ▾
                  {(pendingRegistrations > 0 || pendingTaxDocs > 0 || pendingStoreClub > 0) && (
                    <span className="nav-badge nav-badge-dropdown">{pendingRegistrations + pendingTaxDocs + pendingStoreClub}</span>
                  )}
                </button>
                {salesMenuOpen && (
                  <div className="nav-dropdown-menu">
                    <Link to="/customers" className={`nav-dropdown-item ${isActive('/customers') && !isActive('/customers/store-club') && !isActive('/customers/pending') && !isActive('/customers/tax-documents') ? 'active' : ''}`} onClick={handleNavClick}>
                      Customers
                    </Link>
                    <Link to="/customers/store-club" className={`nav-dropdown-item ${isActive('/customers/store-club') ? 'active' : ''}`} onClick={handleNavClick}>
                      Store Club
                      {pendingStoreClub > 0 && <span className="nav-badge">{pendingStoreClub}</span>}
                    </Link>
                    {pendingRegistrations > 0 && (
                      <Link to="/customers/pending-registrations" className="nav-dropdown-item nav-dropdown-pending" onClick={handleNavClick}>
                        Pending Registrations
                        <span className="nav-badge">{pendingRegistrations}</span>
                      </Link>
                    )}
                    {pendingTaxDocs > 0 && (
                      <Link to="/customers/tax-documents-review" className="nav-dropdown-item nav-dropdown-tax" onClick={handleNavClick}>
                        Tax Documents
                        <span className="nav-badge">{pendingTaxDocs}</span>
                      </Link>
                    )}
                    <Link to="/orders" className={`nav-dropdown-item ${isActive('/orders') ? 'active' : ''}`} onClick={handleNavClick}>
                      Orders
                    </Link>
                    {isOfficeStaff && (
                      <>
                        <Link to="/sales/invoices" className={`nav-dropdown-item ${isActive('/sales/invoices') ? 'active' : ''}`} onClick={handleNavClick}>
                          Invoices
                        </Link>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* NEW ORDER - Highlighted Standalone */}
              <Link to="/order-entry" className="nav-link nav-link-highlight" onClick={handleNavClick}>
                New Order
              </Link>

              {/* OPERATIONS Dropdown */}
              <div className="nav-dropdown"
                onMouseEnter={() => handleDropdownEnter(setOperationsMenuOpen)}
                onMouseLeave={() => handleDropdownLeave(setOperationsMenuOpen)}
              >
                <button
                  className={`nav-link nav-dropdown-trigger ${isDropdownActive(['/production', '/batches', '/bills-of-lading', '/inventory', '/planner']) ? 'active' : ''}`}
                  onClick={() => { closeAllDropdowns(); setOperationsMenuOpen(!operationsMenuOpen); }}
                >
                  Operations ▾
                </button>
                {operationsMenuOpen && (
                  <div className="nav-dropdown-menu">
                    <Link to="/production" className={`nav-dropdown-item ${isActive('/production') ? 'active' : ''}`} onClick={handleNavClick}>
                      Production
                    </Link>
                    <Link to="/batches" className={`nav-dropdown-item ${isActive('/batches') ? 'active' : ''}`} onClick={handleNavClick}>
                      Batches
                    </Link>
                    <Link to="/bills-of-lading" className={`nav-dropdown-item ${isActive('/bills-of-lading') ? 'active' : ''}`} onClick={handleNavClick}>
                      Bills of Lading
                    </Link>
                    <Link to="/inventory" className={`nav-dropdown-item ${isActive('/inventory') ? 'active' : ''}`} onClick={handleNavClick}>
                      Inventory
                    </Link>
                    <Link to="/planner" className={`nav-dropdown-item ${isActive('/planner') ? 'active' : ''}`} onClick={handleNavClick}>
                      Farmhand
                    </Link>
                    <Link to="/planner/feed-alerts" className={`nav-dropdown-item ${isActive('/planner/feed-alerts') ? 'active' : ''}`} onClick={handleNavClick}>
                      Feed Alerts
                      {feedAlertCount > 0 && <span className="nav-badge">{feedAlertCount}</span>}
                    </Link>
                  </div>
                )}
              </div>

              {/* PRODUCTS Dropdown */}
              <div className="nav-dropdown"
                onMouseEnter={() => handleDropdownEnter(setProductsMenuOpen)}
                onMouseLeave={() => handleDropdownLeave(setProductsMenuOpen)}
              >
                <button
                  className={`nav-link nav-dropdown-trigger ${isDropdownActive(['/feed-formulas', '/settings/pricing']) ? 'active' : ''}`}
                  onClick={() => { closeAllDropdowns(); setProductsMenuOpen(!productsMenuOpen); }}
                >
                  Products ▾
                </button>
                {productsMenuOpen && (
                  <div className="nav-dropdown-menu">
                    <Link to="/feed-formulas" className={`nav-dropdown-item ${isActive('/feed-formulas') ? 'active' : ''}`} onClick={handleNavClick}>
                      Feed Formulas
                    </Link>
                    {isOfficeStaff && (
                      <Link to="/settings/pricing" className={`nav-dropdown-item ${isActive('/settings/pricing') ? 'active' : ''}`} onClick={handleNavClick}>
                        Pricing
                      </Link>
                    )}
                  </div>
                )}
              </div>

              {/* REPORTS - Standalone */}
              <Link to="/reports" className={`nav-link ${isActive('/reports') ? 'active' : ''}`} onClick={handleNavClick}>
                Reports
              </Link>

              {/* ADMIN Dropdown */}
              <div className="nav-dropdown"
                onMouseEnter={() => handleDropdownEnter(setAdminMenuOpen)}
                onMouseLeave={() => handleDropdownLeave(setAdminMenuOpen)}
              >
                <button
                  className={`nav-link nav-dropdown-trigger ${isDropdownActive(['/users', '/crm', '/knowledge', '/settings', '/admin/security', '/admin/emails', '/admin/bridge-club', '/admin/partner-api', '/admin/resources', '/admin/agreements', '/admin/support-tickets', '/admin/announcements', '/admin/partner-promotions']) ? 'active' : ''}`}
                  onClick={() => { closeAllDropdowns(); setAdminMenuOpen(!adminMenuOpen); }}
                >
                  Admin ▾
                  {pendingTaxExemptions > 0 && (
                    <span className="nav-badge nav-badge-dropdown">{pendingTaxExemptions}</span>
                  )}
                </button>
                {adminMenuOpen && (
                  <div className="nav-dropdown-menu">
                    {adminGroups.map(group => {
                      const visibleItems = group.items.filter(it => it.show);
                      if (visibleItems.length === 0) return null;   // group with no permitted items
                      const expanded = openAdminGroup === group.key;
                      return (
                        <div key={group.key}>
                          <button
                            type="button"
                            className={`nav-dropdown-group-header ${groupHasActive(group) ? 'active' : ''}`}
                            onClick={() => setOpenAdminGroup(expanded ? null : group.key)}
                            aria-expanded={expanded}
                          >
                            <span>{group.label}</span>
                            <span className="nav-dropdown-group-caret">{expanded ? '▾' : '▸'}</span>
                          </button>
                          {expanded && visibleItems.map(item => (
                            <Link
                              key={item.to}
                              to={item.to}
                              className={`nav-dropdown-item nav-dropdown-subitem ${itemActive(item) ? 'active' : ''}`}
                              onClick={handleNavClick}
                            >
                              {item.label}
                              {item.badge != null && item.badge > 0 && <span className="nav-badge">{item.badge}</span>}
                            </Link>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Mobile-only: Switch Portal Section */}
              <div className="mobile-portal-section">
                <div className="mobile-portal-divider">
                  <span>Switch Portal</span>
                </div>
                <a href="/portal/login" className="nav-link mobile-portal-link portal-customer" onClick={handleNavClick}>
                  Customer Portal
                </a>
                {canAccessMillPortal && (
                  <Link to="/mill/dashboard" className="nav-link mobile-portal-link portal-mill" onClick={handleNavClick}>
                    Mill Portal
                  </Link>
                )}
                {canAccessStorePortal && (
                  <Link to="/store/dashboard" className="nav-link mobile-portal-link portal-store" onClick={handleNavClick}>
                    Store Portal
                  </Link>
                )}
                {canManageDelivery && (
                  <Link to="/delivery/dashboard" className="nav-link mobile-portal-link portal-delivery" onClick={handleNavClick}>
                    Delivery Portal
                  </Link>
                )}
                <button onClick={handleLogout} className="nav-link mobile-logout-btn">
                  Logout
                </button>
              </div>
            </nav>
          </div>
        </div>
      )}

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)} />
      )}
    </header>
  );
};

export default Header;
